

/* ==========================================================================\n   RUNTIME CASE / RESULT LOOKUP INDEXES (/database/js/runtime-index.js)\n\n   Kept outside state.js deliberately. The Explorer can therefore deploy this\n   optimization without adding new named exports to the long-lived state module,\n   avoiding fatal ES-module link errors when a browser/CDN briefly serves mixed\n   asset generations.\n   ========================================================================== */

import { state } from '../state.js?v=20260819-audit2';
import { slugifyUrlSegment, caseRouteSlug } from '../utils.js?v=20260819-audit2';

const caseBySlug = new Map();
const caseIndexBySlug = new Map();
const caseByName = new Map();
const resultByIdx = new Map();

function normalizeName(value) {
  return String(value || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeSlug(value) {
  return slugifyUrlSegment(String(value || ''));
}

export function rebuildCaseLookupIndexes(cases = state.fetchedCasesGlobal) {
  caseBySlug.clear();
  caseIndexBySlug.clear();
  caseByName.clear();

  if (!Array.isArray(cases)) return;
  const legacyAliases = new Map();

  // Canonical backend case.slug owns navigation. The generator now validates
  // uniqueness before publishing, so a duplicate here is a deployment mismatch.
  cases.forEach((caseObj, idx) => {
    if (!caseObj || !caseObj.name) return;
    const canonical = caseRouteSlug(caseObj);
    if (canonical && canonical !== 'build') {
      if (caseBySlug.has(canonical) && caseBySlug.get(canonical) !== caseObj) {
        console.error('[SFF DB] Duplicate canonical case slug in runtime catalog:', canonical);
      } else {
        caseBySlug.set(canonical, caseObj);
        caseIndexBySlug.set(canonical, idx);
      }
    }

    // Transitional aliases are recovery-only and are registered only when unique.
    const aliases = [caseObj._slug, caseObj.case_slug, caseObj.db_slug, slugifyUrlSegment(caseObj.name)]
      .map(normalizeSlug).filter(slug => slug && slug !== 'build' && slug !== canonical);
    for (const alias of new Set(aliases)) {
      const rows = legacyAliases.get(alias) || [];
      rows.push({ caseObj, idx });
      legacyAliases.set(alias, rows);
    }

    const nameKey = normalizeName(caseObj.name);
    if (nameKey && !caseByName.has(nameKey)) caseByName.set(nameKey, { caseObj, idx });
  });

  for (const [alias, rows] of legacyAliases.entries()) {
    if (rows.length !== 1 || caseBySlug.has(alias)) continue;
    caseBySlug.set(alias, rows[0].caseObj);
    caseIndexBySlug.set(alias, rows[0].idx);
  }
}

export function getCaseBySlug(slug) {
  return caseBySlug.get(normalizeSlug(slug)) || null;
}

export function getCaseIndexBySlug(slug) {
  const idx = caseIndexBySlug.get(normalizeSlug(slug));
  return Number.isInteger(idx) ? idx : -1;
}

export function getCaseByName(name) {
  return caseByName.get(normalizeName(name))?.caseObj || null;
}

export function getCaseIndexByName(name) {
  const idx = caseByName.get(normalizeName(name))?.idx;
  return Number.isInteger(idx) ? idx : -1;
}

export function setFilteredResults(results) {
  state.filteredResults = Array.isArray(results) ? results : [];
  resultByIdx.clear();
  for (const result of state.filteredResults) {
    if (result && Number.isInteger(result.idx)) resultByIdx.set(result.idx, result);
  }
  return state.filteredResults;
}

export function getFilteredResult(idx) {
  return resultByIdx.get(Number(idx)) || null;
}

export function clearRuntimeIndexes() {
  caseBySlug.clear();
  caseIndexBySlug.clear();
  caseByName.clear();
  resultByIdx.clear();
}
